import { useEffect, useState, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PriceInput } from "@/components/PriceInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CircleDollarSign, Loader2, Search, CheckCircle2, Wand2, Save, FileSpreadsheet, Percent, Trash2, Upload, Download, SlidersHorizontal, HelpCircle, ChevronDown } from "lucide-react";
import ProductThumb from "@/components/ProductThumb";
import { Pagination } from "@/components/Pagination";
import { curSym } from "@/lib/company";

// 5 satış kanalı; ürün YALNIZ satıldığı kanallarda fiyatlanır (girilen kanallar kaydedilir).
const CH = [
  { key: "market", api: "market_retail", label: "Market", short: "Market" },
  { key: "web", api: "market_web", label: "E-Tic.", short: "E-Ticaret" },
  { key: "marketplace", api: "marketplace", label: "P.yeri", short: "Pazaryeri" },
  { key: "toptan", api: "wholesale_pickup", label: "Depo", short: "Depo/Toptan" },
  { key: "rota", api: "wholesale_delivered", label: "Rota", short: "Rota" },
];
const API2KEY = Object.fromEntries(CH.map((c) => [c.api, c.key]));
const RETAIL_KEY = API2KEY["market_retail"];

export default function Pricing() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("");
  const [activeSub, setActiveSub] = useState("");
  const [fillAll, setFillAll] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [savingAll, setSavingAll] = useState(false);
  const [margin, setMargin] = useState("");
  const [templates, setTemplates] = useState([]);
  const [csvOpen, setCsvOpen] = useState(false);
  const [channelMargins, setChannelMargins] = useState(null);
  const [marginsOpen, setMarginsOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [mode, setMode] = useState("table"); // table | wizard
  const [initialTotal, setInitialTotal] = useState(0);

  const load = () => {
    setLoading(true);
    api.get("/needs-pricing").then((r) => { setList(r.data); setInitialTotal(r.data.length); setLoading(false); }).catch(() => setLoading(false));
  };
  const loadTpl = () => api.get("/price-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  const loadMargins = () => api.get("/pricing/channel-margins").then((r) => setChannelMargins(r.data.margins)).catch(() => {});
  useEffect(() => { load(); loadTpl(); loadMargins(); }, []);

  const cats = useMemo(() => {
    const m = new Map();
    list.forEach((p) => { const c = p.category || "Diğer"; m.set(c, (m.get(c) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [list]);
  useEffect(() => { if (cats.length && !cats.find((c) => c[0] === activeCat)) setActiveCat(cats[0][0]); }, [cats, activeCat]);
  useEffect(() => { setActiveSub(""); }, [activeCat]);

  const subs = useMemo(() => {
    const m = new Map();
    list.filter((p) => (p.category || "Diğer") === activeCat)
      .forEach((p) => { const s = p.subcategory || "—"; m.set(s, (m.get(s) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [list, activeCat]);

  const setRow = (pid, k, v) => setRows((m) => ({ ...m, [pid]: { ...m[pid], [k]: v } }));

  // En az bir kanal fiyatı girilmişse kaydet (tüm kanallar zorunlu DEĞİL).
  const buildPayload = (p) => {
    const r = rows[p.id] || {};
    const payload = {};
    for (const c of CH) { const v = Number(r[c.key]); if (v > 0) payload[c.api] = v; }
    return Object.keys(payload).length ? payload : null;
  };

  const save = async (p) => {
    const payload = buildPayload(p);
    if (!payload) return toast.error("En az bir kanal fiyatı girin (fiyat > 0)");
    setSaving(p.id);
    try {
      const res = await api.post(`/products/${p.id}/prices`, payload);
      if (res.data?.pending_approval) toast.info(res.data.message || "Fiyat değişikliği onaya gönderildi");
      else toast.success(`${p.name} fiyatlandı`);
      setList((l) => l.filter((x) => x.id !== p.id));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const visible = list.filter((p) =>
    (p.category || "Diğer") === activeCat &&
    (!activeSub || (p.subcategory || "—") === activeSub) &&
    (!q || (p.name || "").toLowerCase().includes(q.toLowerCase()) || (p.sku || "").toLowerCase().includes(q.toLowerCase())));

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const paged = visible.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);
  useEffect(() => { setPage(1); }, [activeCat, activeSub, q]);

  const fillRows = (mut) => setRows((m) => { const next = { ...m }; visible.forEach((p) => { next[p.id] = mut(p, { ...next[p.id] }); }); return next; });

  const applyFillAll = () => {
    const v = Number(fillAll);
    if (!(v > 0)) return toast.error("Geçerli bir fiyat girin");
    fillRows((_p, cur) => { CH.forEach((c) => { cur[c.key] = String(v); }); return cur; });
    toast.success(`${visible.length} ürünün tüm kanalları ${v} ${curSym()} dolduruldu`);
  };

  const applyMargin = () => {
    const m = Number(margin);
    if (!(m >= 0)) return toast.error("Geçerli bir marj % girin");
    let filled = 0, skipped = 0;
    fillRows((p, cur) => {
      const cost = Number(p.last_cost) || 0;
      if (cost > 0) { const price = Math.round(cost * (1 + m / 100) * 100) / 100; CH.forEach((c) => { cur[c.key] = String(price); }); filled++; }
      else skipped++;
      return cur;
    });
    toast.success(`${filled} ürün maliyet×%${m} ile dolduruldu${skipped ? ` · ${skipped} ürün maliyetsiz atlandı` : ""}`);
  };

  const applyChannelMargins = () => {
    if (!channelMargins) return toast.error("Kanal marjları yüklenemedi");
    let filled = 0, skipped = 0;
    fillRows((p, cur) => {
      const cost = Number(p.last_cost) || 0;
      if (cost > 0) { CH.forEach((c) => { const mg = Number(channelMargins[c.api]) || 0; cur[c.key] = String(Math.round(cost * (1 + mg / 100) * 100) / 100); }); filled++; }
      else skipped++;
      return cur;
    });
    toast.success(`${filled} ürün kanal marjlarıyla önerildi${skipped ? ` · ${skipped} maliyetsiz atlandı` : ""}`);
  };

  // Perakende (market_retail) fiyatını girip diğer boş kanallara başlangıç değeri olarak kopyalar.
  const copyRetailToChannels = () => {
    let filled = 0;
    fillRows((_p, cur) => {
      const base = Number(cur[RETAIL_KEY]);
      if (base > 0) { CH.forEach((c) => { if (c.key !== RETAIL_KEY && !(Number(cur[c.key]) > 0)) cur[c.key] = String(base); }); filled++; }
      return cur;
    });
    toast.success(filled ? `${filled} üründe Perakende fiyatı boş kanallara kopyalandı` : "Önce Perakende fiyatı girin");
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/products/prices/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = "groot_fiyatlar.csv"; a.click(); URL.revokeObjectURL(url);
      toast.success("Fiyatlar CSV olarak indirildi");
    } catch { toast.error("Dışa aktarma başarısız"); }
  };

  const applyTemplate = (t) => {
    const cf = {};
    Object.entries(t.prices || {}).forEach(([apiKey, v]) => { const k = API2KEY[apiKey]; if (k) cf[k] = String(v); });
    fillRows((_p, cur) => { Object.entries(cf).forEach(([k, v]) => { cur[k] = v; }); return cur; });
    toast.success(`"${t.name}" şablonu ${visible.length} ürüne uygulandı`);
  };
  const delTemplate = async (t) => { try { await api.delete(`/price-templates/${t.id}`); loadTpl(); } catch { /* noop */ } };

  const saveAllVisible = async () => {
    const ready = visible.map((p) => ({ p, payload: buildPayload(p) })).filter((x) => x.payload);
    if (!ready.length) return toast.error("Kaydedilecek (fiyatı girilmiş) satır yok");
    setSavingAll(true);
    let ok = 0, fail = 0;
    for (const { p, payload } of ready) {
      try { await api.post(`/products/${p.id}/prices`, payload); ok++; setList((l) => l.filter((x) => x.id !== p.id)); }
      catch { fail++; }
    }
    setSavingAll(false);
    toast.success(`${ok} ürün kaydedildi${fail ? ` · ${fail} hata` : ""}`);
  };

  const goCat = (dir) => {
    const idx = cats.findIndex((c) => c[0] === activeCat);
    const ni = idx + dir;
    if (ni >= 0 && ni < cats.length) { setActiveCat(cats[ni][0]); setPage(1); }
  };
  const saveAndNext = async () => {
    const idx = cats.findIndex((c) => c[0] === activeCat);
    const nextName = cats[idx + 1]?.[0] || null;
    await saveAllVisible();
    if (nextName) { setActiveCat(nextName); setPage(1); }
  };
  const catIdx = cats.findIndex((c) => c[0] === activeCat);
  const pricedSession = Math.max(0, initialTotal - list.length);
  const progressPct = initialTotal ? Math.round((pricedSession / initialTotal) * 100) : 0;

  return (
    <div className="space-y-5 fade-in" data-testid="pricing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-brand-green"><CircleDollarSign className="h-5 w-5" /></div>
          <div>
            <p className="overline">Fiyat Yönetimi</p>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Toplu Fiyatlandırma</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="pricing-export-btn" onClick={exportCsv} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
            <Download className="mr-1.5 h-4 w-4" /> Dışa Aktar
          </Button>
          <Button data-testid="pricing-csv-open" onClick={() => setCsvOpen(true)} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" /> CSV/Excel ile İçe Aktar
          </Button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="pricing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün / SKU ara..." className="w-52 pl-9" />
          </div>
          <div className="rounded-lg bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700 ring-1 ring-inset ring-amber-200" data-testid="pricing-total">{list.length} fiyatsız</div>
        </div>
      </div>

      <div className="rounded-md bg-emerald-50 px-3 py-2 text-[11px] text-emerald-700 ring-1 ring-inset ring-emerald-200">
        Ürünü yalnız <b>satıldığı kanallarda</b> fiyatlayın — tüm kanalları girmek zorunda değilsiniz. Girdiğiniz kanallar kaydedilir; en az bir kanal yeterlidir.
      </div>

      {list.length > 0 && (
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 w-fit" data-testid="pricing-mode-toggle">
          {[["table", "Tablo Görünümü"], ["wizard", "Sihirbaz"]].map(([k, l]) => (
            <button key={k} data-testid={`pricing-mode-${k}`} onClick={() => setMode(k)}
              className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${mode === k ? "bg-white text-brand-green shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="h-40 animate-pulse rounded-xl bg-slate-100" />
      ) : list.length === 0 ? (
        <div className="rounded-xl bg-white p-12 text-center ring-1 ring-inset ring-slate-200" data-testid="pricing-empty">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <p className="mt-3 text-sm font-medium text-slate-700">Tüm ürünlerin fiyatı tanımlı 🎉</p>
        </div>
      ) : (
        <>
          {mode === "wizard" && (
            <div data-testid="pricing-wizard-bar" className="space-y-3 rounded-xl border border-brand-green/30 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Wand2 className="h-4 w-4 text-brand-green" /> Fiyatlama Sihirbazı</p>
                <span className="text-xs font-semibold text-slate-500" data-testid="pricing-wizard-progress-label">{pricedSession}/{initialTotal} ürün fiyatlandı · %{progressPct}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-brand-green transition-all" style={{ width: `${progressPct}%` }} data-testid="pricing-wizard-progress-bar" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-slate-600">
                  <span className="font-bold text-slate-800">Adım {catIdx + 1}/{cats.length}</span>
                  <span className="mx-1.5 text-slate-300">·</span>
                  <span className="font-semibold text-brand-green">{activeCat}</span>
                  <span className="ml-1.5 text-xs text-slate-400">({visible.length} ürün)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="pricing-wizard-prev" onClick={() => goCat(-1)} disabled={catIdx <= 0} variant="outline" size="sm" className="border-slate-300">Önceki</Button>
                  <Button data-testid="pricing-wizard-skip" onClick={() => goCat(1)} disabled={catIdx >= cats.length - 1} variant="outline" size="sm" className="border-slate-300">Atla →</Button>
                  <Button data-testid="pricing-wizard-save-next" onClick={saveAndNext} disabled={savingAll} size="sm" className="bg-brand-green text-white hover:bg-brand-green-600">
                    {savingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Kaydet & Sonraki
                  </Button>
                </div>
              </div>
              <p className="rounded-md bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-700">Aşağıdaki <b>Hızlı Doldurma Araçları</b> ile bu kategoriyi toplu doldurun, sonra <b>Kaydet &amp; Sonraki</b> ile ilerleyin.</p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2" data-testid="pricing-cat-tabs">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Kategori</label>
            <div className="relative">
              <select data-testid="pricing-cat-select" value={activeCat} onChange={(e) => setActiveCat(e.target.value)}
                className="h-9 w-64 max-w-full appearance-none rounded-md border border-input bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-green">
                {cats.map(([name, count]) => <option key={name} value={name}>{`${name} (${count})`}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
            <span className="text-[11px] text-slate-400">{cats.length} kategori · fiyatsız ürünler kategoriye göre gruplandı</span>
          </div>

          {subs.length > 1 && (
            <div className="flex flex-wrap gap-1.5" data-testid="pricing-subcat-tabs">
              <button data-testid="pricing-subcat-all" onClick={() => setActiveSub("")}
                className={`rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${activeSub === "" ? "bg-slate-800 text-white ring-slate-800" : "bg-white text-slate-500 ring-slate-200"}`}>Tümü</button>
              {subs.map(([name, count]) => (
                <button key={name} data-testid={`pricing-subcat-${name}`} onClick={() => setActiveSub(name)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${activeSub === name ? "bg-slate-800 text-white ring-slate-800" : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"}`}>
                  {name} <span className="opacity-60">{count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Hızlı doldurma araçları */}
          <div className="space-y-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="pricing-bulk-bar">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700"><Wand2 className="h-4 w-4" /> Hızlı Doldurma Araçları</p>
              <button type="button" data-testid="pricing-help-toggle" onClick={() => setShowHelp((s) => !s)} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-700"><HelpCircle className="h-3.5 w-3.5" /> Nasıl çalışır?</button>
            </div>
            {showHelp && (
              <div data-testid="pricing-help" className="space-y-1 rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-slate-600 ring-1 ring-inset ring-emerald-100">
                <p><b className="text-brand-green">Tüm Kanallara {curSym()}:</b> Kutuya yazdığınız <b>tek fiyatı</b> görünen tüm ürünlerin 5 kanalına (Market, E-Ticaret, Pazaryeri, Depo, Rota) aynen doldurur. Örn. 25 yazarsanız hepsi 25 {curSym()} olur.</p>
                <p><b className="text-amber-700">Marj %:</b> Yazdığınız oranı her ürünün <b>maliyetine</b> ekleyip satış fiyatı önerir. Örn. maliyet 20 {curSym()} + %30 → 26 {curSym()}. Maliyeti olmayan ürün atlanır (maliyet mal kabulde girilir).</p>
                <p><b className="text-amber-700">Kanal Marjı Öner:</b> Her kanal için ayrı hedef marjı (dişli ikonundan ayarlanır) maliyete uygular; kanallara <b>farklı</b> fiyat önerir.</p>
                <p><b className="text-sky-700">Perakendeden Türet:</b> Önce Market (perakende) fiyatını girin; bu araç onu <b>boş kalan</b> diğer kanallara kopyalar.</p>
                <p className="text-slate-400">Not: Bu araçlar yalnızca kutuları <b>doldurur</b>; kalıcı olması için satırdaki <b>Kaydet</b> ya da <b>Görünenleri Kaydet</b>'e basın.</p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Wand2 className="h-4 w-4 text-brand-green" />
              <Input data-testid="pricing-fill-all-input" type="number" min="0" value={fillAll} onChange={(e) => setFillAll(e.target.value)} placeholder={`Tüm kanallara ${curSym()}`} className="h-9 w-36" />
              <Button data-testid="pricing-fill-all-btn" onClick={applyFillAll} variant="outline" size="sm" className="border-brand-green text-brand-green hover:bg-emerald-50">Tüm Kanallara Uygula</Button>
              <span className="mx-1 text-slate-300">|</span>
              <Percent className="h-4 w-4 text-amber-600" />
              <Input data-testid="pricing-margin-input" type="number" min="0" value={margin} onChange={(e) => setMargin(e.target.value)} placeholder="Marj %" className="h-9 w-24" />
              <Button data-testid="pricing-margin-btn" onClick={applyMargin} variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50">Maliyet × Marj Doldur</Button>
              <Button data-testid="pricing-channel-margin-btn" onClick={applyChannelMargins} variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50">Kanal Marjı Öner</Button>
              <Button data-testid="pricing-copy-retail-btn" onClick={copyRetailToChannels} variant="outline" size="sm" className="border-sky-300 text-sky-700 hover:bg-sky-50">Perakendeden Türet</Button>
              <Button data-testid="pricing-margins-config" onClick={() => setMarginsOpen(true)} variant="ghost" size="sm" className="text-slate-500" title="Kanal marjlarını düzenle"><SlidersHorizontal className="h-4 w-4" /></Button>
              <span className="mx-1 text-slate-300">|</span>
              <Button data-testid="pricing-save-all" onClick={saveAllVisible} disabled={savingAll} size="sm" className="bg-brand-green text-white hover:bg-brand-green-600">
                {savingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Görünenleri Kaydet
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-emerald-200/60 pt-2">
              <span className="text-xs font-semibold text-slate-500">Şablon:</span>
              {templates.map((t) => (
                <span key={t.id} className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs ring-1 ring-inset ring-slate-200">
                  <button data-testid={`pricing-tpl-apply-${t.id}`} onClick={() => applyTemplate(t)} className="font-medium text-slate-700 hover:text-brand-green">{t.name}</button>
                  <button data-testid={`pricing-tpl-del-${t.id}`} onClick={() => delTemplate(t)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
              {templates.length === 0 && <span className="text-xs text-slate-400">yok</span>}
            </div>
          </div>

          <div className="rounded-xl bg-white ring-1 ring-inset ring-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-5 py-3">
              <p className="font-heading text-sm font-bold text-slate-700">{activeCat}{activeSub ? ` · ${activeSub}` : ""}</p>
              <p className="text-xs text-slate-400">{visible.length} ürün</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-400">
                  <tr className="border-b border-slate-100">
                    <th className="px-5 py-2.5 font-medium">Ürün</th>
                    <th className="px-3 py-2.5 font-medium">Maliyet</th>
                    {CH.map((c) => (
                      <th key={c.key} className="px-2 py-2.5 text-right font-medium">
                        <div>{c.label} {curSym()}</div>
                      </th>
                    ))}
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {visible.length === 0 && (<tr><td colSpan={8} className="px-5 py-8 text-center text-sm text-slate-400">Bu seçimde eşleşen ürün yok.</td></tr>)}
                  {paged.map((p) => (
                    <tr key={p.id} data-testid={`pricing-row-${p.id}`} className="hover:bg-emerald-50/30">
                      <td className="px-5 py-2.5">
                        <div className="group relative inline-block">
                          <p className="cursor-default font-medium text-slate-800" data-testid={`pricing-name-${p.id}`}>{p.name}</p>
                          <div data-testid={`pricing-hover-${p.id}`} className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden w-44 rounded-lg border border-slate-200 bg-white p-2 shadow-xl group-hover:block">
                            <ProductThumb product={p} className="h-28 w-full rounded-md" />
                            <p className="mt-1.5 truncate text-xs font-semibold text-slate-700">{p.name}</p>
                            <p className="text-[11px] text-slate-400">{p.subcategory || p.category || "—"}</p>
                          </div>
                        </div>
                        <p className="font-mono text-[10px] text-slate-400">{p.sku}</p>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{Number(p.last_cost) > 0 ? `${p.last_cost} ${curSym()}` : "—"}</td>
                      {CH.map((c) => (
                        <td key={c.key} className="px-2 py-2.5 text-right">
                          <PriceInput data-testid={`pricing-${c.key}-${p.id}`} type="number" min="0" value={rows[p.id]?.[c.key] ?? ""}
                            onChange={(e) => setRow(p.id, c.key, e.target.value)} wrapperClassName="ml-auto w-[84px]" className="h-9 w-full tabular text-right" placeholder="0" />
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-right">
                        <Button data-testid={`pricing-save-${p.id}`} onClick={() => save(p)} disabled={saving === p.id} size="sm" className="bg-brand-green text-white hover:bg-brand-green-600">
                          {saving === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {visible.length > 0 && (
            <div className="rounded-xl bg-white px-4 py-2 ring-1 ring-inset ring-slate-200">
              <Pagination page={pageSafe} pageSize={pageSize} total={visible.length}
                onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }}
                sizes={[25, 50, 100]} allowAll testid="pricing-pagination" />
            </div>
          )}
        </>
      )}

      <CsvImportDialog open={csvOpen} onClose={() => setCsvOpen(false)} onDone={() => { setCsvOpen(false); load(); }} />
      <MarginsDialog open={marginsOpen} onClose={() => setMarginsOpen(false)} margins={channelMargins} onSaved={setChannelMargins} />
    </div>
  );
}

function CsvImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (mode) => {
    if (!file) return toast.error("CSV/Excel dosyası seçin");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("mode", mode);
      const { data } = await api.post("/products/prices/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (mode === "apply") { toast.success(`${data.applied} ürün fiyatlandı`); onDone(); setFile(null); setPreview(null); }
      else { setPreview(data); toast.success(`Önizleme: ${data.matched} eşleşti, ${data.unmatched} eşleşmedi`); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="csv-import-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><FileSpreadsheet className="h-5 w-5 text-brand-green" /> CSV / Excel ile Toplu Fiyat</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <DialogDescription className="text-xs text-slate-500">Kolonlar: <b>SKU</b> (veya Barkod) + <b>Market / E-Ticaret / Pazaryeri / Depo / Rota</b>. Yalnız dolu kanallar uygulanır; ürünler SKU ile eşleştirilir.</DialogDescription>
          <input data-testid="csv-file-input" type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand-green file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-white" />
          {file && <p className="text-xs text-emerald-600">{file.name}</p>}
          <div className="flex gap-2">
            <Button data-testid="csv-preview-btn" onClick={() => send("preview")} disabled={busy || !file} variant="outline" className="border-slate-300">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Önizle
            </Button>
            <Button data-testid="csv-apply-btn" onClick={() => send("apply")} disabled={busy || !preview || preview.matched === 0} className="bg-brand-green text-white hover:bg-brand-green-600">Uygula ({preview?.matched || 0})</Button>
          </div>
          {preview && (
            <div className="rounded-lg border border-slate-200 p-3 text-xs" data-testid="csv-preview">
              <p className="font-semibold text-slate-700">Kanallar: {(preview.channels || []).join(", ") || "—"}</p>
              <p className="mt-1 text-emerald-600">Eşleşen: <b>{preview.matched}</b> · Eşleşmeyen: <b className="text-red-500">{preview.unmatched}</b> · Toplam satır: {preview.total_rows}</p>
              {(preview.sample || []).slice(0, 8).map((s, i) => (
                <div key={i} className="mt-1 flex justify-between border-t border-slate-100 pt-1">
                  <span className="truncate">{s.name} <span className="font-mono text-slate-400">{s.sku}</span></span>
                  <span className="tabular text-slate-500">{Object.values(s.prices).join(" / ")} {curSym()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MarginsDialog({ open, onClose, margins, onSaved }) {
  const [vals, setVals] = useState({});
  useEffect(() => {
    if (open && margins) setVals(Object.fromEntries(Object.entries(margins).map(([k, v]) => [k, String(v)])));
  }, [open, margins]);
  const save = async () => {
    const m = {};
    CH.forEach((c) => { const v = Number(vals[c.api]); if (v >= 0) m[c.api] = v; });
    try {
      const { data } = await api.put("/pricing/channel-margins", { margins: m });
      toast.success("Kanal marjları kaydedildi"); onSaved(data.margins); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="margins-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><SlidersHorizontal className="h-5 w-5 text-brand-green" /> Kanal Varsayılan Marjları</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          <DialogDescription className="text-xs text-slate-500">Her kanal için hedef marj %. "Kanal Marjı Öner" bu oranları ürün maliyetine uygulayarak her kanala farklı fiyat önerir (maliyet mal kabulde girilir).</DialogDescription>
          {CH.map((c) => (
            <div key={c.key} className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-600">{c.short}</span>
              <div className="flex items-center gap-1">
                <Input data-testid={`margin-${c.key}`} type="number" min="0" value={vals[c.api] ?? ""} onChange={(e) => setVals((m) => ({ ...m, [c.api]: e.target.value }))} className="h-9 w-24 tabular" />
                <span className="text-slate-400">%</span>
              </div>
            </div>
          ))}
          <Button data-testid="margins-save" onClick={save} className="w-full bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-2 h-4 w-4" /> Kaydet</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
